import {
    IndexedPageView,
    GetGroupInfoRequest,
    GetGroupResponse,
    EmailAddressWrapper,
    getServiceAsync,
    TFactories,
} from '../../../../service';

import {
    PrivateDistributionList,
    PublicDistributionList,
    PersonType,
    makeHubPersonaId,
    runOwsRequestWithMonitor,
    Feature,
} from '../../../../shared';
import { createPrivateDistributionListOrMember } from './../converters/personaConverter';

const defaultPageView: IndexedPageView = {
    BasePoint: 'Beginning',
    Offset: 0,
};

export type DistributionListIdentifier =
    | { type: PersonType.PublicDistributionList; emailAddress: EmailAddressWrapper }
    | { type: PersonType.PrivateDistributionList; itemId: string };

export default function getDistributionListInfo(
    personaId: string,
    contactFolderId: string,
    listIdentifier: DistributionListIdentifier
): Promise<PrivateDistributionList | PublicDistributionList> {
    // For some reason, Owners are part of the GroupInfo ResultSet. We need to make two calls and merge results for Public DLs.
    const makeMembersRequest = () => getDistributionListMembers(contactFolderId, listIdentifier);

    const makeInfoRequest = () =>
        listIdentifier.type === PersonType.PrivateDistributionList
            ? Promise.resolve<GetGroupResponse>(undefined)
            : getPublicDistributionListInfo(listIdentifier);

    return Promise.all([makeMembersRequest(), makeInfoRequest()]).then(
        ([membersResponse, infoResponse]) => {
            let distributionList: PrivateDistributionList | PublicDistributionList;
            if (listIdentifier.type === PersonType.PrivateDistributionList) {
                const privateDistributionList: PrivateDistributionList = {
                    type: PersonType.PrivateDistributionList,
                    hubPersonaId: makeHubPersonaId(personaId),
                    itemId: listIdentifier.itemId,
                    personaId,
                    members:
                        (membersResponse.Members &&
                            membersResponse.Members.map(createPrivateDistributionListOrMember)) ||
                        [],
                    info: {
                        displayNameFirstLast: undefined,
                        displayNameLastFirst: undefined,
                        listOriginalDisplayName: undefined,
                    },
                    notes: undefined,
                };
                distributionList = privateDistributionList;
            } else {
                const owners =
                    (infoResponse.Owners &&
                        infoResponse.Owners.map(createPrivateDistributionListOrMember)) ||
                    [];
                const members =
                    (membersResponse.Members &&
                        membersResponse.Members.map(createPrivateDistributionListOrMember).filter(
                            member =>
                                !owners.some(
                                    owner =>
                                        owner.emailAddressWrapper.EmailAddress ===
                                        member.emailAddressWrapper.EmailAddress
                                )
                        )) ||
                    [];
                const publicDistributionList: PublicDistributionList = {
                    type: PersonType.PublicDistributionList,
                    hubPersonaId: makeHubPersonaId(personaId),
                    personaId,
                    itemId: undefined,
                    owners,
                    members,
                    emailAddress: listIdentifier.emailAddress.EmailAddress,
                    info: {
                        listOriginalDisplayName: undefined,
                        displayNameFirstLast: undefined,
                        displayNameLastFirst: undefined,
                    },
                };
                distributionList = publicDistributionList;
            }
            return distributionList;
        }
    );
}

const makeRequest = (
    listIdentifier: DistributionListIdentifier,
    contactFolderId: string,
    resultSet: number /* 1 = GroupInfo, 2 = Members */,
    serviceFactory: TFactories
): GetGroupInfoRequest => {
    const request: GetGroupInfoRequest = {
        Paging: serviceFactory.indexedPageView(defaultPageView),
        AdObjectId: null,
        EmailAddress:
            listIdentifier.type === PersonType.PublicDistributionList
                ? listIdentifier.emailAddress
                : undefined,
        ItemId:
            listIdentifier.type === PersonType.PrivateDistributionList
                ? serviceFactory.itemId({ Id: listIdentifier.itemId })
                : undefined,
        ResultSet: resultSet,
    };

    // Parent folder is needed for Public Folders
    if (contactFolderId) {
        request.ParentFolderId = serviceFactory.targetFolderId({
            BaseFolderId: serviceFactory.folderId({ Id: contactFolderId }),
        });
    }

    return request;
};

function getDistributionListMembers(
    contactFolderId: string,
    listIdentifier: DistributionListIdentifier
): Promise<GetGroupResponse> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'DistributionListsMembers',
            feature: Feature.PrivateDistributionList,
            operation: () => {
                return service.Operation.getGroupInfoOperation({
                    getGroupInfoRequest: makeRequest(
                        listIdentifier,
                        contactFolderId,
                        2 /* Members */,
                        service.Factory
                    ),
                });
            },
            transformResponse: (response: GetGroupResponse) => response,
        })
    );
}

function getPublicDistributionListInfo(
    listIdentifier: DistributionListIdentifier
): Promise<GetGroupResponse> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'DistributionListsInfo',
            feature: Feature.PrivateDistributionList,
            operation: () => {
                return service.Operation.getGroupInfoOperation({
                    getGroupInfoRequest: makeRequest(
                        listIdentifier,
                        null /* contactFolderId */,
                        1 /* GroupInfo */,
                        service.Factory
                    ),
                });
            },
            transformResponse: (response: GetGroupResponse) => response,
        })
    );
}
